import { getSnakeys as getSankeys } from '@/api';
import { getChainByChainName } from '@/utils';
import { MenuItem, MenuItem as MuiMenuItem, Select, styled, Typography } from '@mui/material';
import { useRequest, useSize } from 'ahooks';
import classNames from 'classnames';
import { curveBumpX, line } from 'd3-shape';
import { Dispatch, Fragment, SetStateAction, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { colors, typography } from '@/theme';

type Node = {
	id: string;
	name: ChainName;
	flow: { [k: string]: { volume: number; ratio: number } };
	volume: number;
	absoluteVolume: number;
	absoluteRatio: number;
	ratio: number;
	y0: number;
	y1: number;
};

type Link = {
	source: Node;
	target: Node;
	absoluteRatio: number;
	y0: number;
	y1: number;
	volume: number;
};

/**
 * Rescales the ratios of items to ensure a minimum ratio while maintaining proportions.
 */
function rescale<Key extends string, T extends { [k in Key]: number }>(
	items: T[],
	key: Key,
	out: Key,
	minRatio: number,
): void {
	const total = items.reduce((acc, item) => acc + item[key], 0);

	const itemsWithRatio = items.map((flow) => {
		const ratio = flow[key] / total;
		return {
			...flow,
			[out]: ratio,
		};
	});
	const upper = itemsWithRatio.filter((item) => item[out] >= minRatio);
	const exccessRatio = (1 - items.length * minRatio) / upper.reduce((a, b) => a + b[out] - minRatio, 0);

	for (const flow of items) {
		const ratio = flow[key] / total;
		flow[out] = (minRatio + (ratio > minRatio ? (ratio - minRatio) * exccessRatio : 0)) as T[Key];
	}
}

/**
 * Adjusts the ratio of items to ensure a minimum ratio for a specific item.
 */
function adjustRatio<Key extends string, T extends { [k in Key]: number }>(
	items: T[],
	key: Key,
	minRatio: number,
	found: T,
): void {
	if (found[key] >= minRatio) return;
	const ratio = 1 - minRatio + found[key];
	found[key] = minRatio as T[Key];

	items.forEach((item) => {
		if (item === found) return;
		item[key] = (item[key] * ratio) as T[Key];
	});
}

/**
 * Calculates the vertical positions of nodes based on their ratios.
 */
function calculateNodePositions(nodes: Node[], height: number): void {
	let currentY = 0;

	nodes.forEach((node) => {
		const nodeHeight = node.ratio * height;
		node.y0 = currentY;
		node.y1 = currentY + nodeHeight;
		currentY = node.y1;
	});
}

// Components
interface NodeBoxProps {
	node: Node;
	selected: string;
	setSelected: Dispatch<SetStateAction<string>>;
	link?: Link;
	lastItem: boolean;
}

function NodeBox({ node, selected, setSelected, link, lastItem }: NodeBoxProps) {
	const chain = getChainByChainName(node.name);
	if (!chain) {
		return null;
	}
	const isSelected = selected === node.id;
	const isOut = node.id.startsWith('out');
	const sameSide = selected.startsWith(node.id.split('-')[0]);
	const ratio = sameSide ? node.absoluteRatio : link ? link.absoluteRatio : 0;
	const volume = sameSide ? node.absoluteVolume : (link?.volume ?? 0);

	return (
		<div
			key={node.id}
			className={classNames(
				'px-3 absolute w-full flex items-center gap-2 select-none',
				isSelected ? 'bg-backgroundTilesOverlay/15' : 'cursor-pointer hover:bg-backgroundSecondary',
				isOut ? 'flex-row-reverse' : 'flex-row',
				!lastItem && 'border-b border-borderSecondary',
			)}
			style={{
				top: node.y0,
				height: node.y1 - node.y0,
				transitionProperty: 'background-color, top, left, right, bottom, height',
				transitionDuration: '150ms',
			}}
			onClick={() => setSelected(node.id)}
		>
			<img src={chain.logoURI} alt={node.name} width={20} />
			<Typography variant='appSmallBold' className='text-textPrimary'>
				{chain.chainName}
			</Typography>
			<div className='flex-1 w-full' />
			{ratio && volume ? (
				<div
					className={classNames(
						'flex flex-col',
						selected.startsWith('out') ? 'items-start' : 'items-end',
						'transition',
						isSelected ? 'text-textPrimary' : 'text-textSecondary',
						sameSide ? '' : 'absolute',
						!sameSide && (isOut ? '-left-2 -translate-x-full' : '-right-2 translate-x-full'),
					)}
					style={{ transitionProperty: 'left, right, transform' }}
				>
					<Typography variant='appSmallRegular' className='text-textPrimary'>
						{(ratio * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%
					</Typography>
					<Typography variant='appTinyRegular' className='text-textPrimary'>
						${volume.toLocaleString('en-US', { maximumFractionDigits: 2 })}
					</Typography>
				</div>
			) : null}
		</div> 
	);
}

export default function Graph() {
	const [range, setRange] = useState<'24h' | '7d' | '30d'>('7d');
	const { data: rawData } = useRequest(() => getSankeys(range), { refreshDeps: [range] });
	const containerRef = useRef<HTMLDivElement>(null);
	const { width, height } = useSize(containerRef.current) ?? { width: 600, height: 400 };
	const nodePadding = 3;
	const [selected, setSelected] = useState<string>('in-ethereum');

	const totals = useMemo(() => {
		if (!rawData) return null;
		return Object.entries(rawData).map(([id, value]) => {
			const totalIn = value.inFlow.reduce((acc, b) => acc + b.volume, 0);
			const totalOut = value.outFlow.reduce((acc, b) => acc + b.volume, 0);

			const inFlow = Object.fromEntries(
				value.inFlow.map(({ chain, volume }) => [chain, { volume, ratio: volume / totalIn }]),
			);
			const outFlow = Object.fromEntries(
				value.outFlow.map(({ chain, volume }) => [chain, { volume, ratio: volume / totalOut }]),
			);
			return {
				id,
				inFlow,
				outFlow,
				totalIn,
				totalOut,
			};
		});
	}, [rawData]);

	const graphData = useMemo(() => {
		if (!totals) return null;
		const isOut = selected.startsWith('out');

		const inNodes: Node[] = [];
		const outNodes: Node[] = [];

		const total = totals.reduce((acc, t) => acc + t.totalIn, 0);
		for (const { id, totalIn, inFlow, totalOut, outFlow } of totals) {
			if (Object.keys(outFlow).length > 0) {
				inNodes.push({
					id: `in-${id}`,
					volume: totalOut,
					absoluteVolume: totalOut,
					absoluteRatio: totalOut / total,
					name: id as ChainName,
					flow: outFlow,
					ratio: 0,
					y0: 0,
					y1: 0,
				});
			}
			if (Object.keys(inFlow).length > 0 ) {
				outNodes.push({
					id: `out-${id}`,
					volume: totalIn,
					absoluteVolume: totalIn,
					absoluteRatio: totalIn / total,
					name: id as ChainName,
					flow: inFlow,
					ratio: 0,
					y0: 0,
					y1: 0,
				});
			}
		}

		const targetNodes = isOut ? inNodes : outNodes;
		const sourceNodes = isOut ? outNodes : inNodes;
		const source = sourceNodes.find(({ id }) => id === selected)!;
		if (!source) {
			return null;
		}

		// Set the absoluteVolume of other side to repsective flow of selected chain
		for (const node of targetNodes) {
			node.absoluteVolume = source.flow[node.name]?.volume ?? 0;
		}

		const minRatio = 0.07;
		const selectedRatio = 0.1;
		rescale(inNodes, 'absoluteVolume', 'ratio', minRatio);
		rescale(outNodes, 'absoluteVolume', 'ratio', minRatio);
		// Adjust the ratio of selected node to ensure a minimum ratio
		adjustRatio(sourceNodes, 'ratio', selectedRatio, source);

		// Use absoluteVolume if we want to orders to change based on link size
		inNodes.sort((a, b) => b.volume - a.volume);
		outNodes.sort((a, b) => b.volume - a.volume);

		calculateNodePositions(inNodes, height);
		calculateNodePositions(outNodes, height);

		let y0 = source.y0;
		let y1 = source.y0;
		const diff = source.y1 - source.y0;

		const links: Link[] = [];
		for (const target of targetNodes) {
			y1 = y0 + target.ratio * diff;
			const volume = target.absoluteVolume;
			if (volume) {
				links.push({
					source,
					target,
					absoluteRatio: volume / source.absoluteVolume,
					volume,
					y0,
					y1,
				});
				y0 = y1;
			}
		}
		if (links[links.length - 1]) {
			links[links.length - 1].y1 = source.y1;
		}

		return { inNodes, outNodes, links };
	}, [totals, selected, height]);

	const isOut = selected.startsWith('out');
	const leftWidth = isOut ? 140 : 240;
	const rightWidth = isOut ? 240 : 140;

	// D3 line generator for creating link paths
	const linkGenerator = line().curve(curveBumpX);
	const link = (d: Link, start = false) => {
		const { target } = d;

		const x0 = 0;
		const x1 = width - leftWidth - rightWidth;

		const link1 = d.y1;
		const target1 = target.y1 - nodePadding;
		const target0 = target.y0 + nodePadding;
		const link0 = d.y0;

		const points: [number, number][] = start
			? [
				// left bottom
				[x0, link0],
				// right bottom
				[x0 + 10, link0],
				// right top
				[x0 + 10, link1],
				// left top
				[x0, link1],
			]
			: [
				// left bottom
				[x0, isOut ? target0 : link0],
				// right bottom
				[x1, isOut ? link0 : target0],
				// right top
				[x1, isOut ? link1 : target1],
				// left top
				[x0, isOut ? target1 : link1],
			];

		return linkGenerator(points) + 'Z';
	};

	return (
		<div className='h-full flex items-stretch flex-col'>
			<div className='h-12 flex items-center justify-center border-b border-borderPrimary'>
				<Typography
					variant='appSmallRegular'
					className={classNames(
						'text-center flex-none transition-all',
						isOut ? 'text-textSecondary' : 'text-textPrimary',
					)}
					style={{ flexBasis: leftWidth, transitionProperty: 'flex-basis, color' }}
				>
					Source
				</Typography>
				<div className='flex-1 w-full flex items-center justify-center'>
					<Select
						value={range}
						onChange={(e) => setRange(e.target.value as '24h' | '7d' | '30d')}
						variant="standard"
						disableUnderline={true}
						MenuProps={{
							classes: {
								paper: 'bg-backgroundPrimary border border-solid border-borderPrimary rounded-none',
							},
						}}
						SelectDisplayProps={{
							className: classNames('focus:bg-backgroundPrimary')
						}}
					>
						<MenuItem value='24h'>Past 24 hours flow</MenuItem>
						<MenuItem value='7d'>Past week flow</MenuItem>
						<MenuItem value='30d'>Past 30 days volume</MenuItem>
					</Select>
				</div>
				<Typography
					variant='appSmallRegular'
					className={classNames(
						'text-center flex-none transition-all',
						isOut ? 'text-textPrimary' : 'text-textSecondary',
					)}
					style={{ flexBasis: rightWidth, transitionProperty: 'flex-basis' }}
				>
					Destination
				</Typography>
			</div>
			<div className='relative flex-1 flex' ref={containerRef}>
				{graphData ? (
					<>
						{/* Left column (input nodes) */}
						<div className='relative h-full' style={{ width: leftWidth }}>
							{graphData.inNodes.map((d, index) => (
								<NodeBox
									key={d.id}
									node={d}
									lastItem={index === graphData.inNodes.length - 1}
									selected={selected}
									setSelected={setSelected}
									link={graphData.links.find((l) => l.target.id === d.id)}
								/>
							))}
						</div>

						{/* SVG for link paths */}
						<svg width={width - leftWidth - rightWidth} height={height}>
							<defs>
								<linearGradient id='Gradient2' x1='0' x2='1' y1='0' y2='0'>
									<stop offset='0%' stopColor={colors.backgroundTilesOverlay} stopOpacity={0.4} />
									<stop offset='100%' stopColor={colors.backgroundTilesOverlay} stopOpacity={0} />
								</linearGradient>
							</defs>
							<g className='link-group'>
								<AnimatePresence>
									{graphData.links.map((d, i) => {
										const path = link(d, false);
										const startPath = link(d, true);
										const key = `${i}-${d.source.id}-${d.target.id}`;

										return (
											<motion.path
												key={key}
												initial={{ d: startPath, fillOpacity: 1 }}
												animate={{ d: path, fillOpacity: 1 }}
												exit={{
													fillOpacity: 0,
													transition: { ease: 'easeIn', duration: 0.01 },
												}}
												transition={{ duration: 0.15 + 0.03 * i }}
												stroke='none'
												fill='url(#Gradient2)'
											/>
										);
									})}
								</AnimatePresence>
							</g>
						</svg>

						{/* Right column (output nodes) */}
						<div className='relative h-full' style={{ width: rightWidth }}>
							{graphData.outNodes.map((d, index) => (
								<NodeBox
									key={d.id}
									node={d}
									lastItem={index === graphData.outNodes.length - 1}
									selected={selected}
									setSelected={setSelected}
									link={graphData.links.find((l) => l.target.id === d.id)}
								/>
							))}
						</div>
					</>
				) : null}
			</div>
		</div>
	);
}
